/**
 * «Enviar plantilla» (change `conversaciones-templates`, Fase 2, D13, D17): lo único que sale fuera de la
 * ventana de 24 h en un canal Coexistence. El selector ofrece las copias aprobadas de este canal, el
 * operador completa las variables, ve el texto final y la categoría que Meta le dio, y manda.
 */
import { useEffect, useMemo, useState } from "react";
import { useGetIdentity } from "@refinedev/core";
import { Alert, Button, DatePicker, Flex, Input, Modal, Select, Space, Spin, Tag, Typography } from "antd";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { ExclamationCircleFilled, LeftOutlined, SendOutlined } from "@ant-design/icons";
import { conversationsApi } from "@/services/conversations.api";
import { contextApi, orderStatusDot, quoteStatusTag } from "@/services/context.api";
import {
  TIPOS_DE_DATO,
  TemplateOfrecible,
  avisoDeCategoria,
  categoriaDe,
  ofrecibles,
  parametrosCompletos,
  parametrosIniciales,
  renderizar,
  tipoDeDato,
  variablesPorDefecto,
} from "@/pages/channels/cloud-templates";

const { Text } = Typography;

// La fecha se guarda como el cliente la lee (DD/MM/YYYY) y se vuelve a leer para el calendario.
dayjs.extend(customParseFormat);

/** Los tipos que el operador elige de una lista del contacto en vez de tipear (#519). */
type TipoConLista = "quote_number" | "order_number" | "delivery_address";
const CON_LISTA: TipoConLista[] = ["quote_number", "order_number", "delivery_address"];
const esConLista = (t: string): t is TipoConLista => (CON_LISTA as string[]).includes(t);
const SIN_NINGUNO: Record<TipoConLista, string> = {
  quote_number: "presupuestos",
  order_number: "pedidos",
  delivery_address: "direcciones de entrega",
};
type Opcion = { value: string; label: string };

const avisoSinLista = (tipo: TipoConLista, falta?: "sin_contacto" | "fallo") =>
  falta === "sin_contacto"
    ? `Este chat no tiene un contacto del Store asociado, así que no hay ${SIN_NINGUNO[tipo]} para elegir: escribilo a mano. `
    : falta === "fallo"
      ? `No se pudo traer la lista de ${SIN_NINGUNO[tipo]} del contacto: escribilo a mano. `
      : `Este contacto no tiene ${SIN_NINGUNO[tipo]} en este Store: escribilo a mano. `;

/** Por qué una lista quedó sin opciones: cambia lo que se le dice al operador. */
class SinLista extends Error {
  constructor(readonly motivo: "sin_contacto" | "fallo") {
    super(motivo);
  }
}

/** La lista de un tipo, del más reciente al más viejo: el primero es el que viene elegido. */
async function opcionesDe(tipo: TipoConLista, contacto: { contactId?: number; userId?: number; storeCode: string }): Promise<Opcion[]> {
  const { contactId, userId, storeCode } = contacto;
  if (tipo === "quote_number") {
    if (!contactId) throw new SinLista("sin_contacto");
    const quotes = await contextApi.quotesByContact(contactId, storeCode);
    return quotes.map((q) => {
      // Sin nombre (o con el "-" de relleno) no se muestra el guion suelto.
      const nombre = String(q.name ?? "").trim().replace(/^-+$/, "");
      const estado = quoteStatusTag(q.state)?.label;
      return { value: String(q.id), label: `#${q.id}${nombre ? ` - ${nombre}` : ""}${estado ? ` · ${estado}` : ""}` };
    });
  }
  if (!userId) throw new SinLista("sin_contacto");
  if (tipo === "order_number") {
    const orders = await contextApi.ordersByContact(userId, storeCode);
    return orders.map((o) => ({
      value: o.incrementId,
      label: `#${o.incrementId}${o.name && !/^-+$/.test(o.name.trim()) ? ` - ${o.name.trim()}` : ""} · ${orderStatusDot(o.status).label}`,
    }));
  }
  const direcciones = await contextApi.deliveryAddresses(userId, storeCode);
  return direcciones.map((d) => ({ value: d.linea, label: d.nombre ? `${d.linea} · ${d.nombre}` : d.linea }));
}

/** El rótulo que pone `variablesPorDefecto` cuando quien creó la plantilla no escribió "Qué dato es". */
const ROTULO_GENERICO = /^Variable \d+$/;

/**
 * Dónde cae la variable en el texto (#513): unas palabras antes y después, con el hueco marcado.
 * Es la explicación que siempre está, aunque la plantilla no tenga un rótulo propio.
 */
function contextoDe(body: string, n: number): string | null {
  const marca = `{{${n}}}`;
  const i = body.indexOf(marca);
  if (i < 0) return null;
  const limpio = (t: string) => t.replace(/\{\{\d+\}\}/g, "…").replace(/\s+/g, " ");
  const antes = limpio(body.slice(0, i)).trimStart().split(" ");
  const despues = limpio(body.slice(i + marca.length)).trimEnd().split(" ");
  const a = antes.slice(-4).join(" ");
  const d = despues.slice(0, 4).join(" ");
  return `${antes.length > 4 ? "…" : ""}${a}___${d}${despues.length > 4 ? "…" : ""}`;
}

export const TemplatePicker = ({
  open,
  onClose,
  conversationId,
  channelId,
  contactName,
  contactId,
  userId,
  storeCode,
  storeName,
  sellers = [],
  onSent,
  onError,
}: {
  open: boolean;
  onClose: () => void;
  conversationId: string;
  channelId: string;
  contactName: string | null | undefined;
  /** El contacto en el Store, para sus presupuestos (`contactId`), pedidos y direcciones (`userId`). */
  contactId?: number;
  userId?: number;
  storeCode: string;
  storeName?: string;
  /** Los Sellers del contacto (sus participaciones): el primero viene elegido. */
  sellers?: string[];
  /** El mensaje que devolvió el backend, para sumarlo al hilo. */
  onSent: (sent: unknown) => void;
  /** El error del envío: lo trata el chat, que sabe qué le cambia. */
  onError: (e: unknown) => void;
}) => {
  const [lista, setLista] = useState<TemplateOfrecible[] | null>(null);
  const [falla, setFalla] = useState<string | null>(null);
  const [elegida, setElegida] = useState<string>();
  const [params, setParams] = useState<string[]>([]);
  const [enviando, setEnviando] = useState(false);

  useEffect(() => {
    if (!open) return;
    let vigente = true;
    setLista(null);
    setFalla(null);
    conversationsApi
      .listTemplates(channelId)
      .then((v) => {
        if (!vigente) return;
        const ok = ofrecibles(v.items);
        setLista(ok);
        setElegida(ok[0]?.publication.publicationId);
      })
      .catch((e) => {
        if (!vigente) return;
        const status = (e as { response?: { status?: number } })?.response?.status;
        setFalla(status === 404 ? "Este ambiente todavía no tiene plantillas." : "No se pudieron cargar las plantillas.");
        setLista([]);
      });
    return () => {
      vigente = false;
    };
  }, [open, channelId]);

  const actual = useMemo(() => lista?.find((o) => o.publication.publicationId === elegida) ?? null, [lista, elegida]);
  const { data: identity } = useGetIdentity<{ name?: string }>();
  useEffect(() => {
    setParams(actual ? parametrosIniciales(actual.definition, contactName, { operador: identity?.name, store: storeName, seller: sellers[0] }) : []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actual, contactName, identity?.name, storeName, sellers.join(",")]);

  const variables = actual
    ? actual.definition.variables.length
      ? actual.definition.variables
      : variablesPorDefecto(actual.definition.body)
    : [];
  const tipos = variables.map(tipoDeDato);

  /** Las listas del contacto que pide la plantilla elegida; una que no carga queda vacía y se tipea. */
  const [opciones, setOpciones] = useState<Partial<Record<TipoConLista, Opcion[]>>>({});
  const [faltas, setFaltas] = useState<Partial<Record<TipoConLista, SinLista["motivo"]>>>({});
  useEffect(() => {
    if (!open) setOpciones({});
  }, [open]);
  const tiposConLista = tipos.filter(esConLista).join(",");
  useEffect(() => {
    if (!open || !tiposConLista) return;
    let vigente = true;
    // Se vuelven a pedir cada vez que cambia el contacto: el chat lo carga después de abrir, y una
    // lista pedida sin contacto quedaba vacía para siempre.
    setOpciones({});
    setFaltas({});
    for (const tipo of tiposConLista.split(",") as TipoConLista[]) {
      opcionesDe(tipo, { contactId, userId, storeCode })
        .catch((e) => {
          // Que se distinga «no tiene» de «no se pudo traer»: los dos dejan el campo para tipear.
          if (!(e instanceof SinLista)) console.error(`No se pudo traer la lista de ${tipo}`, e);
          if (vigente) setFaltas((prev) => ({ ...prev, [tipo]: e instanceof SinLista ? e.motivo : "fallo" }));
          return [] as Opcion[];
        })
        .then((ops) => vigente && setOpciones((prev) => ({ ...prev, [tipo]: ops })));
    }
    return () => {
      vigente = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, tiposConLista, contactId, userId, storeCode]);
  // Viene elegido el más reciente; lo que el operador ya completó no se pisa.
  useEffect(() => {
    setParams((prev) => prev.map((p, i) => (p || !esConLista(tipos[i]) ? p : opciones[tipos[i] as TipoConLista]?.[0]?.value ?? "")));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opciones, actual]);
  const preview = actual ? renderizar(actual.publication.renderedBody, params) : "";
  const cat = categoriaDe(actual?.publication.category);
  const aviso = avisoDeCategoria(actual?.publication ?? null);
  const listo = Boolean(actual) && parametrosCompletos(params);

  const enviar = async () => {
    if (!actual) return;
    setEnviando(true);
    try {
      const sent = await conversationsApi.sendTemplateMessage(
        conversationId,
        actual.publication.publicationId,
        params.map((p) => p.trim()),
      );
      onSent(sent);
      onClose();
    } catch (e) {
      onError(e);
    } finally {
      setEnviando(false);
    }
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title="Enviar una plantilla"
      destroyOnClose
      footer={
        <Flex justify="space-between" gap={8}>
          <Button icon={<LeftOutlined />} onClick={onClose}>
            Atrás
          </Button>
          <Button type="primary" icon={<SendOutlined />} loading={enviando} disabled={!listo} onClick={() => void enviar()} style={{ background: listo ? "#00733B" : undefined }}>
            Enviar
          </Button>
        </Flex>
      }
    >
      <Flex vertical gap={12}>
        <Text type="secondary">
          Cada plantilla tiene un costo según su tipo: <strong>Utility</strong> (seguimiento de algo que pidió el
          cliente) es la más barata; <strong>Marketing</strong> (promociones) es la más cara. El tipo lo define Meta.
        </Text>
        {lista === null && <Spin />}
        {falla && <Alert type="warning" showIcon message={falla} />}
        {lista && !falla && lista.length === 0 && (
          <Alert
            type="info"
            showIcon
            message="Este canal no tiene plantillas aprobadas"
            description="Se ven en la ficha del canal, en «Plantillas de WhatsApp». Las del catálogo de xcons se publican solas al conectar el número."
          />
        )}
        {lista && lista.length > 0 && (
          <>
            <div>
              <Text style={{ display: "block", marginBottom: 4 }}>Seleccioná una plantilla</Text>
              <Select
                value={elegida}
                onChange={setElegida}
                style={{ width: "100%" }}
                options={lista.map((o) => ({
                  value: o.publication.publicationId,
                  label: `${o.definition.name}${categoriaDe(o.publication.category)?.texto === "Marketing" ? " · marketing" : ""}`,
                }))}
              />
            </div>
            {actual && (
              <>
                {variables.map((v, i) => {
                  // Qué poner (#513): el tipo de dato de la plantilla, y dónde cae en el texto.
                  const contexto = contextoDe(actual.publication.renderedBody, v.n);
                  const ejemplo = actual.definition.examples[i]?.trim();
                  const tipo = tipos[i];
                  const cambiar = (valor: string) => setParams((prev) => prev.map((x, j) => (j === i ? valor : x)));
                  const ops = esConLista(tipo) ? opciones[tipo] : undefined;
                  return (
                    <div key={v.n}>
                      <Text style={{ display: "block" }}>
                        {tipo !== "otro" ? TIPOS_DE_DATO[tipo].rotulo : ROTULO_GENERICO.test(v.label) ? `Variable ${v.n}` : v.label}
                      </Text>
                      <Text type="secondary" style={{ display: "block", fontSize: 12, marginBottom: 4 }}>
                        {tipo !== "otro" && TIPOS_DE_DATO[tipo].solo && "Ya viene completo, cambialo si hace falta. "}
                        {ops && ops.length === 0 && avisoSinLista(tipo as TipoConLista, faltas[tipo as TipoConLista])}
                        {contexto && <>Va en: «{contexto}»</>}
                      </Text>
                      {tipo === "date" ? (
                        // La fecha la elige el operador (#519), en el formato en que la lee el cliente.
                        <DatePicker
                          format="DD/MM/YYYY"
                          value={params[i] && dayjs(params[i], "DD/MM/YYYY").isValid() ? dayjs(params[i], "DD/MM/YYYY") : null}
                          onChange={(d) => cambiar(d ? d.format("DD/MM/YYYY") : "")}
                          placeholder="Seleccionar día"
                          style={{ width: "100%" }}
                        />
                      ) : tipo === "seller_name" && sellers.length > 1 ? (
                        // Con más de un Seller, se elige cuál; viene el primero.
                        <Select
                          value={params[i] || undefined}
                          options={sellers.map((s) => ({ value: s, label: s }))}
                          onChange={cambiar}
                          style={{ width: "100%" }}
                        />
                      ) : esConLista(tipo) && ops?.length !== 0 ? (
                        // Un desplegable con lo del contacto (#519), con buscador. Sin nada que elegir, se tipea.
                        <Select
                          showSearch
                          value={params[i] || undefined}
                          options={ops ?? []}
                          onChange={cambiar}
                          loading={!ops}
                          placeholder={ops ? "Elegí uno" : "Cargando…"}
                          optionFilterProp="label"
                          style={{ width: "100%" }}
                        />
                      ) : (
                        <Input
                          value={params[i] ?? ""}
                          placeholder={ejemplo ? `Ej.: ${ejemplo}` : undefined}
                          onChange={(e) => cambiar(e.target.value)}
                          maxLength={500}
                        />
                      )}
                    </div>
                  );
                })}
                <div style={{ background: "#f5f5f5", borderRadius: 8, padding: "8px 12px", whiteSpace: "pre-wrap" }}>
                  {preview}
                  {actual.publication.renderedFooter && (
                    <Text type="secondary" style={{ display: "block", fontSize: 12, marginTop: 4 }}>
                      {actual.publication.renderedFooter}
                    </Text>
                  )}
                </div>
                <Space size={6}>
                  <Text type="secondary">Categoría:</Text>
                  {cat ? <Tag color={cat.texto === "Marketing" ? "volcano" : "default"}>{cat.texto}</Tag> : <Text type="secondary">Meta todavía no la informó</Text>}
                  {aviso && (
                    <Text type="warning" style={{ fontSize: 12 }}>
                      <ExclamationCircleFilled style={{ marginRight: 4 }} />
                      {aviso}
                    </Text>
                  )}
                </Space>
              </>
            )}
          </>
        )}
      </Flex>
    </Modal>
  );
};
